import { useCallback, useEffect, useRef, useState } from "react";
import { photosApi, ApiError } from "@/api";
import type { PhotoSet, SitePhoto } from "@/types";
import { Button } from "@/components/ui";
import { formatBytes } from "@/lib/shares";
import { formatDayHeading, formatTakenTime } from "@/lib/photos";

/** Full-screen viewer over the visible photo order. Opens the original via a
 *  fresh 300 s presigned URL per photo (the 512px thumbnail sits underneath,
 *  blurred, until the original has loaded), with ← / → keys, swipe, caption
 *  editing, move-to-set, delete and download (saved as the original file). A
 *  native <dialog> so Escape and focus trapping come for free. */
export function PhotoLightbox({
  photos,
  index,
  sets,
  onIndex,
  onClose,
  onCaption,
  onMove,
  onDelete,
  onDownload,
}: {
  photos: SitePhoto[];
  index: number;
  sets: PhotoSet[];
  onIndex: (i: number) => void;
  onClose: () => void;
  onCaption: (photo: SitePhoto, caption: string) => Promise<void>;
  onMove: (photo: SitePhoto, setId: number) => Promise<void>;
  onDelete: (photo: SitePhoto) => Promise<void>;
  onDownload: (photo: SitePhoto) => Promise<void>;
}) {
  const photo = photos[index] as SitePhoto | undefined;
  const photoId = photo?.id ?? 0;
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [fullUrl, setFullUrl] = useState<string | null>(null);
  const [fullLoaded, setFullLoaded] = useState(false);
  const [fullError, setFullError] = useState(false);
  const [caption, setCaption] = useState(photo?.caption ?? "");
  const [editingCaption, setEditingCaption] = useState(false);
  const [busy, setBusy] = useState<"" | "caption" | "move" | "delete" | "download">("");
  const [error, setError] = useState<string | null>(null);
  const touchStartX = useRef<number | null>(null);

  useEffect(() => {
    const d = dialogRef.current;
    if (d && !d.open) d.showModal();
  }, []);

  // A fresh signed URL per photo; reset the per-photo UI state with it.
  useEffect(() => {
    if (!photoId) return;
    let cancelled = false;
    setFullUrl(null);
    setFullLoaded(false);
    setFullError(false);
    setEditingCaption(false);
    setError(null);
    photosApi
      .getPhotoDownloadUrl(photoId)
      .then(({ url }) => {
        if (!cancelled) setFullUrl(url);
      })
      .catch(() => {
        if (!cancelled) setFullError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [photoId]);

  useEffect(() => {
    setCaption(photo?.caption ?? "");
  }, [photoId, photo?.caption]);

  const prev = useCallback(() => {
    if (index > 0) onIndex(index - 1);
  }, [index, onIndex]);
  const next = useCallback(() => {
    if (index < photos.length - 1) onIndex(index + 1);
  }, [index, photos.length, onIndex]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (editingCaption) return;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        prev();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        next();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [prev, next, editingCaption]);

  if (!photo) return null;

  const fail = (err: unknown, fallback: string) => setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : fallback);

  async function saveCaption() {
    if (!photo || busy) return;
    setBusy("caption");
    setError(null);
    try {
      await onCaption(photo, caption);
      setEditingCaption(false);
    } catch (err) {
      fail(err, "Could not save the caption.");
    } finally {
      setBusy("");
    }
  }

  async function move(setId: number) {
    if (!photo || busy || setId === photo.photo_set_id) return;
    setBusy("move");
    setError(null);
    try {
      await onMove(photo, setId);
    } catch (err) {
      fail(err, "Could not move the photo.");
    } finally {
      setBusy("");
    }
  }

  async function remove() {
    if (!photo || busy) return;
    if (!window.confirm("Delete this photo? This can’t be undone.")) return;
    setBusy("delete");
    setError(null);
    try {
      await onDelete(photo);
    } catch (err) {
      fail(err, "Could not delete the photo.");
      setBusy("");
    }
  }

  async function download() {
    if (!photo || busy) return;
    setBusy("download");
    setError(null);
    try {
      await onDownload(photo);
    } catch (err) {
      fail(err, "Could not download the original.");
    } finally {
      setBusy("");
    }
  }

  function onTouchStart(e: React.TouchEvent) {
    touchStartX.current = e.touches[0]?.clientX ?? null;
  }
  function onTouchEnd(e: React.TouchEvent) {
    const start = touchStartX.current;
    touchStartX.current = null;
    const end = e.changedTouches[0]?.clientX;
    if (start == null || end == null) return;
    const dx = end - start;
    if (dx > 50) prev();
    else if (dx < -50) next();
  }

  const dims = photo.width && photo.height ? `${photo.width}×${photo.height}` : "";

  return (
    <dialog
      ref={dialogRef}
      className="photo-lightbox"
      aria-label={photo.caption || photo.file_name}
      onClose={onClose}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="photo-lightbox__stage" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <img className={`photo-lightbox__thumb${fullLoaded ? " photo-lightbox__thumb--hidden" : ""}`} src={photo.thumb_url} alt="" aria-hidden="true" />
        {fullUrl ? (
          <img
            className={`photo-lightbox__full${fullLoaded ? " photo-lightbox__full--ready" : ""}`}
            src={fullUrl}
            alt={photo.caption || photo.file_name}
            onLoad={() => setFullLoaded(true)}
            onError={() => setFullError(true)}
          />
        ) : null}
        {!fullLoaded && !fullError ? <span className="photo-lightbox__status">Loading full size…</span> : null}
        {fullError ? <span className="photo-lightbox__status">Couldn’t load the full-size image — try Download.</span> : null}
        <button type="button" className="photo-lightbox__nav photo-lightbox__nav--prev" onClick={prev} disabled={index === 0} aria-label="Previous photo">
          ‹
        </button>
        <button type="button" className="photo-lightbox__nav photo-lightbox__nav--next" onClick={next} disabled={index >= photos.length - 1} aria-label="Next photo">
          ›
        </button>
        <button type="button" className="photo-lightbox__close" onClick={onClose} aria-label="Close">
          ✕
        </button>
      </div>

      <div className="photo-lightbox__bar">
        <div className="photo-lightbox__meta">
          <strong className="numeric">
            {index + 1} / {photos.length}
          </strong>
          <span>
            {formatDayHeading(photo.taken_at)} {formatTakenTime(photo.taken_at)}
          </span>
          {dims ? <span className="numeric">{dims}</span> : null}
          <span>{formatBytes(photo.file_size)}</span>
          {photo.uploaded_by ? <span>by {photo.uploaded_by}</span> : null}
          <span className="photo-lightbox__file">{photo.file_name}</span>
        </div>

        <div className="photo-lightbox__caption">
          {editingCaption ? (
            <>
              <input
                className="photo-lightbox__caption-input"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Caption"
                maxLength={300}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") void saveCaption();
                  if (e.key === "Escape") {
                    e.stopPropagation();
                    setCaption(photo.caption);
                    setEditingCaption(false);
                  }
                }}
              />
              <Button size="sm" onClick={() => void saveCaption()} loading={busy === "caption"}>
                Save
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="photo-lightbox__ghost"
                onClick={() => {
                  setCaption(photo.caption);
                  setEditingCaption(false);
                }}
                disabled={busy === "caption"}
              >
                Cancel
              </Button>
            </>
          ) : (
            <button type="button" className="photo-lightbox__caption-btn" onClick={() => setEditingCaption(true)} title="Edit caption">
              {photo.caption || "Add a caption"}
            </button>
          )}
        </div>

        <div className="photo-lightbox__actions">
          <label className="photo-lightbox__move">
            <span>Set</span>
            <select value={String(photo.photo_set_id)} onChange={(e) => void move(Number(e.target.value))} disabled={busy !== ""} aria-label="Move to set">
              {sets.map((s) => (
                <option key={s.id} value={String(s.id)}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
          <Button size="sm" variant="ghost" className="photo-lightbox__ghost" onClick={() => void download()} loading={busy === "download"} disabled={busy !== "" && busy !== "download"}>
            Download
          </Button>
          <Button size="sm" variant="danger" onClick={() => void remove()} loading={busy === "delete"} disabled={busy !== "" && busy !== "delete"}>
            Delete
          </Button>
        </div>
        {error ? (
          <p className="photo-lightbox__error" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </dialog>
  );
}
